import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { Search, Shield, Loader2 } from 'lucide-react'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import { useHasPermission } from '@/lib/hooks/usePermission'
import { getPermissions, getPermissionCategories } from '@/lib/services/rbacService'

export default function PermissionsPage() {
  const { t } = useTranslation()
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<string>('all')

  // Permission check
  const canViewPermissions = useHasPermission(['rbac.permissions.view', 'rbac.roles.view'])

  // Fetch permissions
  const { data: permissionsData, isLoading: isLoadingPermissions } = useQuery({
    queryKey: ['permissions', searchQuery, selectedCategory],
    queryFn: () => {
      const params: { search?: string; category?: string } = {}
      if (searchQuery) params.search = searchQuery
      if (selectedCategory !== 'all') params.category = selectedCategory
      return getPermissions(params)
    },
    enabled: canViewPermissions,
  })

  // Fetch categories
  const { data: categories } = useQuery({
    queryKey: ['permission-categories'],
    queryFn: getPermissionCategories,
    enabled: canViewPermissions,
  })

  if (!canViewPermissions) {
    return (
      <div className="container mx-auto py-8">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">{t('common.accessDenied')}</h1>
          <p className="text-muted-foreground">{t('rbac.noPermissionToViewPermissions')}</p>
        </div>
      </div>
    )
  }

  const permissions = permissionsData?.permissions ?? []
  const categoryList = categories || []

  return (
    <div className="container mx-auto py-8 px-4">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold">{t('rbac.permissionsCatalog')}</h1>
        <p className="text-muted-foreground mt-1">{t('rbac.permissionsCatalogDescription')}</p>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="flex-1 relative">
          <Search
            className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground"
            aria-hidden="true"
          />
          <Input
            type="search"
            placeholder={t('rbac.searchPermissions')}
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="pl-10"
            aria-label={t('rbac.searchPermissions')}
          />
        </div>
        <Select value={selectedCategory} onValueChange={setSelectedCategory}>
          <SelectTrigger className="w-full sm:w-[200px]" aria-label={t('rbac.filterByCategory')}>
            <SelectValue placeholder={t('rbac.allCategories')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('rbac.allCategories')}</SelectItem>
            {categoryList.map(cat => (
              <SelectItem key={cat.category} value={cat.category}>
                {cat.category} ({cat.count})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Loading state */}
      {isLoadingPermissions && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin" aria-hidden="true" />
          <span className="sr-only">{t('common.loading')}</span>
        </div>
      )}

      {/* Table */}
      {!isLoadingPermissions && (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[300px]">{t('rbac.permissionCode')}</TableHead>
                    <TableHead>{t('rbac.description')}</TableHead>
                    <TableHead className="w-[150px]">{t('rbac.category')}</TableHead>
                    <TableHead className="w-[100px]">{t('rbac.type')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {permissions.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                        {searchQuery || selectedCategory !== 'all'
                          ? t('rbac.noPermissionsFound')
                          : t('rbac.noPermissionsYet')}
                      </TableCell>
                    </TableRow>
                  ) : (
                    permissions.map(permission => (
                      <TableRow key={permission.id}>
                        <TableCell className="font-mono text-sm font-medium">
                          {permission.code}
                        </TableCell>
                        <TableCell className="text-sm">{permission.description}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{permission.category || 'Other'}</Badge>
                        </TableCell>
                        <TableCell>
                          {permission.isSystem && (
                            <Badge variant="secondary" className="flex items-center gap-1 w-fit">
                              <Shield className="h-3 w-3" aria-hidden="true" />
                              <span>{t('rbac.system')}</span>
                            </Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {!isLoadingPermissions && permissions.length > 0 && (
        <p className="text-sm text-muted-foreground mt-4">
          {t('rbac.showingPermissions', {
            count: permissions.length,
            total: permissionsData?.total || 0,
          })}
        </p>
      )}
    </div>
  )
}
